import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Table, Typography } from 'antd'
import { Link, generatePath } from 'react-router-dom'
import _ from 'lodash'

import * as ROUTES from 'admin/routes'
import { Queries } from 'admin/lib/Api'
import { AccountTag } from 'admin/lib/Tags'
import { ScreenSpin } from 'admin/lib/ScreenSpin'

import { Tooltip } from 'antd'
import { ApiOutlined, CheckOutlined } from '@ant-design/icons'
import { colours } from '@qflow/theme'

export function PublicApiKeysList({ searchTerm = '' }) {
  const apiKeys = Queries.useApiKeys()

  const filteredApiKeys = useMemo(() => {
    if (!apiKeys.isFetched || !apiKeys.data) {
      return []
    }

    return apiKeys.data.filter(key =>
      searchTerm
        ? key.name?.toLowerCase().includes(searchTerm.toLowerCase())
        : true
    )
  }, [apiKeys.data, apiKeys.isFetched, searchTerm])

  const columns = useMemo(
    /** @returns {import("antd/lib/table").ColumnsType<any>} */
    () => [
      {
        title: 'Enabled',
        dataIndex: 'isEnabled',
        width: '4rem',
        sorter: (a, b) => a.isEnabled - b.isEnabled,
        render: isEnabled => {
          return isEnabled ? '🟢' : '🔴'
        }
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: '20rem',
        sorter: (a, b) => a.name.localeCompare(b.name),
        defaultSortOrder: 'ascend'
      },
      {
        title: (<Tooltip title="Webhook Key"><ApiOutlined
            style={{ fontSize: '1.25rem' }}
          /></Tooltip>),
        dataIndex: 'isWebhookKey',
        width: '3rem',
        render: isWebhookKey => {
          return isWebhookKey ? (
          <CheckOutlined
            style={{ color: colours.GREEN, fontSize: '1.25rem' }}
          />
        ) : null
        }
      },
      {
        title: 'Permissions',
        dataIndex: 'permissions',
        width: '10rem',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: value => (
          <Typography.Text type="secondary">
            {Array.isArray(value) ? _.sortBy(value).join(', ') : String(value)}
          </Typography.Text>
        )
      },
      {
        title: 'Accounts',
        dataIndex: 'apiKeyMemberships',
        render: memberships =>
          memberships.map(({ accountId }) => (
            <AccountTag key={accountId} accountId={accountId} />
          ))
      },
      {
        title: 'Expires',
        dataIndex: 'expiresAt',
        render: expiryIso => {
          const date = new Date(expiryIso)
          const expired = date.valueOf() <= new Date().valueOf()

          return (
            <Typography.Text type={expired ? 'danger' : 'secondary'}>
              {date.toLocaleString()}
            </Typography.Text>
          )
        }
      },
      {
        title: 'Edit',
        dataIndex: 'hashedKey',
        width: '10rem',
        render: id => (
          <Typography.Text key={id}>
            <Link
              to={generatePath(ROUTES.PUBLIC_API_KEYS.EDIT, {
                apiKeyId: id
              })}
            >
              Edit
            </Link>
          </Typography.Text>
        )
      }
    ],
    []
  )

  if (apiKeys.isFetching && !apiKeys.data) {
    return <ScreenSpin />
  }

  return (
    <Table
      columns={columns}
      dataSource={filteredApiKeys}
      rowKey="hashedKey"
      style={{ width: '100%', height: 'auto' }}
    />
  )
}

PublicApiKeysList.propTypes = {
  searchTerm: PropTypes.string
}
